// src/stores/DashboardStore.js - Dashboard Analytics Zustand Store
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useDashboardStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    // Dashboard statistics data
    dashboardStats: null,
    dashboardLoading: false,
    dashboardError: null,

    // Connection state
    isConnected: false,

    // Internal flags
    _initialized: false,
    _socketManager: null,

    // ================================
    // INTERNAL HELPERS
    // ================================

    /**
     * Get socket manager instance
     * @private
     */
    _getSocketManager: () => {
      const { _socketManager } = get();
      return { socketManager: _socketManager };
    },

    /**
     * Set socket manager instance (called during initialization)
     * @private
     */
    _setSocketManager: (socketManager) => {
      set({ _socketManager: socketManager });
    },

    // ================================
    // SOCKET OPERATIONS
    // ================================

    /**
     * Load dashboard statistics - NO RETURN VALUE
     * @param {object} options - Optional date range options
     * @param {string} options.startDate - Start date (YYYY-MM-DD format)
     * @param {string} options.endDate - End date (YYYY-MM-DD format)
     */
    loadDashboardStats: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ DashboardStore: Socket service not initialized');
        return;
      }

      const { startDate, endDate } = options;

      console.log('📊 DashboardStore: Loading dashboard stats with options:', { startDate, endDate });

      set({
        dashboardLoading: true,
        dashboardError: null
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_DASHBOARD_STATS, {
        start_date: startDate || undefined,
        end_date: endDate || undefined
      });
    },

    // ================================
    // UTILITY METHODS
    // ================================

    /**
     * Clear dashboard error
     */
    clearDashboardError: () => {
      set({ dashboardError: null });
    },

    /**
     * Refresh dashboard stats (reload with current options)
     */
    refreshDashboardStats: (options = {}) => {
      const { loadDashboardStats } = get();
      loadDashboardStats(options);
    },
  }))
);

// ================================
// SOCKET LISTENER INITIALIZATION
// ================================

/**
 * Initialize socket event listeners for DashboardStore
 * Called once during app initialization
 *
 * @param {object} socketManager - SocketManager instance
 */
export const initializeDashboardStoreSocketListeners = (socketManager) => {
  console.log('🔌 DashboardStore: Initializing socket listeners');

  // Store the socket manager reference
  useDashboardStore.getState()._setSocketManager(socketManager);

  // ================================
  // DASHBOARD STATS LOADED
  // ================================

  socketManager.on(SOCKET_EVENTS.DASHBOARD_STATS_LOADED, (data) => {
    console.log('✅ DashboardStore: Dashboard stats loaded', data);

    useDashboardStore.setState({
      dashboardStats: data,
      dashboardLoading: false,
      dashboardError: null,
      isConnected: true
    });
  });

  // ================================
  // DASHBOARD STATS FAILED
  // ================================

  socketManager.on(SOCKET_EVENTS.GET_DASHBOARD_STATS_FAILED, (error) => {
    console.error('❌ DashboardStore: Failed to load dashboard stats', error);

    useDashboardStore.setState({
      dashboardLoading: false,
      dashboardError: error.message || 'Failed to load dashboard statistics',
      dashboardStats: null
    });
  });

  // Mark as initialized
  useDashboardStore.setState({
    _initialized: true,
    isConnected: true
  });

  console.log('✅ DashboardStore: Socket listeners initialized');
};

export default useDashboardStore;
